/**
 * The walk-in.
 *
 * ---------------------------------------------------------------------
 * WHAT IS SHORT COMES FIRST
 *
 * A chef opening this at seven in the morning is not browsing an
 * inventory list. They are asking one question — what do I need to order
 * — and the answer is the handful of things below par. So that is the
 * top of the page, and the full list is underneath it.
 *
 * Everything else here is bookkeeping that happens on a delivery bay or
 * a Sunday night, and it can be a form.
 *
 * ---------------------------------------------------------------------
 * A DELIVERY IS KEYED THE WAY THE INVOICE READS
 *
 * Four cases at ninety-two fifty. The form asks for the number of
 * purchase units and the money, shows what that turns into on the shelf
 * BEFORE anything is saved, and shows the resulting unit cost beside it
 * — because a pack size keyed as a can rather than a case is out by a
 * factor of six, and the only moment anybody can catch that is while
 * looking at "612 fl oz" and thinking that seems like a lot.
 *
 * ---------------------------------------------------------------------
 * YIELD IS SHOWN AS MONEY, NOT AS A PERCENTAGE
 *
 * "78%" means nothing at a glance. "$0.75 an ounce, $0.96 usable" is the
 * same fact and it is the one that explains why the ribeye costs what it
 * costs. Both are on the row.
 */

import { useMemo, useState } from 'react';
import { api } from '../lib/api.ts';
import { useApi, useMutation } from '../lib/useApi.ts';
import { money } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { Card, Empty, ErrorBanner, Loading, Status, Tile } from '../components/ui.tsx';

interface Unit {
  id: string;
  code: string;
  name: string;
  plural: string | null;
  dimension: string;
}

interface Category {
  id: string;
  code: string;
  name: string;
  is_alcohol: boolean;
  inventory_account: string;
  cogs_account: string;
}

interface Storeroom {
  id: string;
  code: string;
  name: string;
  is_default: boolean;
  department: string;
}

interface Ingredient {
  id: string;
  code: string;
  name: string;
  category_code: string;
  category: string;
  is_alcohol: boolean;
  stock_unit_code: string;
  stock_unit: string;
  stock_unit_plural: string | null;
  purchase_description: string | null;
  stock_per_purchase: number;
  yield_percent: number;
  on_hand: number;
  par_level: number | null;
  below_par: boolean;
  stock_value_minor: number;
  last_cost_minor: number;
  usable_cost_minor: number;
  preferred_vendor: string | null;
  is_active: boolean;
}

interface InventoryValue {
  category: string;
  is_alcohol: boolean;
  account_number: string;
  lines_held: number;
  value_minor: number;
}

const nf = new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 });

/** A cost per stock unit, which is often a fraction of a cent. */
function perUnit(minor: number | string, unit: string): string {
  const n = Number(minor);
  if (!Number.isFinite(n) || n === 0) return '—';
  return `${n >= 100 ? `$${(n / 100).toFixed(2)}` : `${n.toFixed(1)}¢`}/${unit.toLowerCase()}`;
}

export function KitchenPage() {
  const { currency, atLeast } = useSession();
  const [tab, setTab] = useState<'stock' | 'deliver' | 'waste' | 'counts'>('stock');
  const [category, setCategory] = useState('');

  const mayWrite = atLeast('manager');

  const setup = useApi<{ units: Unit[]; categories: Category[]; storerooms: Storeroom[] }>(
    '/v1/kitchen/setup',
  );
  const list = useApi<{ ingredients: Ingredient[] }>(
    `/v1/kitchen/ingredients${category ? `?category=${category}` : ''}`,
    [category],
  );
  const stock = useApi<{ stock: any[]; value: InventoryValue[] }>('/v1/kitchen/stock');

  const ingredients = list.data?.ingredients ?? [];
  const short = useMemo(() => ingredients.filter((i) => i.below_par), [ingredients]);
  const held = stock.data?.value ?? [];
  const total = held.reduce((n, v) => n + Number(v.value_minor), 0);

  if (setup.loading && !setup.data) return <Loading />;

  return (
    <>
      <h1>Kitchen</h1>
      <ErrorBanner error={setup.error ?? list.error ?? stock.error} />

      <p className="muted small">
        What the kitchen buys and what it is holding. Everything is bought in one unit and
        counted in another — a case of six number tens is 612 fluid ounces — and the pack size on
        each ingredient does that conversion once, so nobody has to do it at the loading dock.
      </p>

      <div className="tiles">
        <Tile label="On the shelves" value={money(total, currency)} />
        <Tile
          label="Below par"
          value={String(short.length)}
          note={short.length > 0 ? 'order these' : 'nothing to order'}
        />
        <Tile label="Ingredients" value={String(ingredients.length)} />
      </div>

      {/* ============================================================ */}
      {short.length > 0 && (
        <Card title="Below Par">
          <div className="table">
            <table className="data data--cards">
              <thead>
                <tr>
                  <th>What</th>
                  <th className="num">On hand</th>
                  <th className="num">Par</th>
                  <th>Buy as</th>
                  <th>From</th>
                </tr>
              </thead>
              <tbody>
                {short.map((i) => (
                  <tr key={i.id}>
                    <td data-label="What" className="cell--title">{i.name}</td>
                    <td data-label="On hand" className="num">
                      {nf.format(Number(i.on_hand))} {i.stock_unit_code}
                    </td>
                    <td data-label="Par" className="num muted">
                      {nf.format(Number(i.par_level))}
                    </td>
                    <td data-label="Buy as" className="muted small">
                      {i.purchase_description ?? '—'}
                    </td>
                    <td data-label="From" className="muted small">
                      {i.preferred_vendor ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <div className="actions">
        {(['stock', 'deliver', 'waste', 'counts'] as const).map((t) => (
          <button
            key={t}
            className={tab === t ? 'small' : 'secondary small'}
            onClick={() => setTab(t)}
          >
            {t === 'stock' ? 'What we hold'
              : t === 'deliver' ? 'A delivery came in'
              : t === 'waste' ? 'Something went in the bin'
              : 'Counts'}
          </button>
        ))}
      </div>

      {tab === 'stock' && (
        <Card
          title="What We Hold"
          actions={
            <select value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="">Everything</option>
              {(setup.data?.categories ?? []).map((c) => (
                <option key={c.id} value={c.code}>{c.name}</option>
              ))}
            </select>
          }
        >
          {ingredients.length === 0 ? (
            <Empty>Nothing yet. Add what the kitchen buys and key the first delivery.</Empty>
          ) : (
            <div className="table">
              <table className="data data--cards">
                <thead>
                  <tr>
                    <th>What</th>
                    <th>Bought as</th>
                    <th className="num">On hand</th>
                    <th className="num">Cost</th>
                    <th className="num">Usable</th>
                    <th className="num">Value</th>
                  </tr>
                </thead>
                <tbody>
                  {ingredients.map((i) => (
                    <tr key={i.id}>
                      <td data-label="What" className="cell--title">
                        {i.name}
                        <span className="muted small"> · {i.category}</span>
                      </td>
                      <td data-label="Bought as" className="muted small">
                        {i.purchase_description ?? '—'}
                        {Number(i.stock_per_purchase) !== 1 && (
                          <span> = {nf.format(Number(i.stock_per_purchase))} {i.stock_unit_code}</span>
                        )}
                      </td>
                      <td data-label="On hand" className="num">
                        {nf.format(Number(i.on_hand))} {i.stock_unit_code}
                      </td>
                      <td data-label="Cost" className="num">
                        {perUnit(i.last_cost_minor, i.stock_unit_code)}
                      </td>
                      <td data-label="Usable" className="num">
                        {Number(i.yield_percent) === 100 ? (
                          <span className="muted">same</span>
                        ) : (
                          <>
                            {perUnit(i.usable_cost_minor, i.stock_unit_code)}
                            <span className="muted small"> · {nf.format(Number(i.yield_percent))}% yield</span>
                          </>
                        )}
                      </td>
                      <td data-label="Value" className="num">
                        {money(i.stock_value_minor, currency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {held.length > 0 && (
            <div className="table" style={{ marginTop: 16 }}>
              <table className="data">
                <thead>
                  <tr>
                    <th>Shelf</th>
                    <th>Account</th>
                    <th className="num">Lines held</th>
                    <th className="num">Value</th>
                  </tr>
                </thead>
                <tbody>
                  {held.map((v) => (
                    <tr key={v.category}>
                      <td className="cell--title">
                        {v.category}
                        {v.is_alcohol && <span className="muted small"> · alcohol</span>}
                      </td>
                      <td className="muted">{v.account_number}</td>
                      <td className="num">{v.lines_held}</td>
                      <td className="num">{money(v.value_minor, currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {tab === 'deliver' && mayWrite && (
        <ReceiveForm
          setup={setup.data!}
          ingredients={ingredients}
          onDone={() => {
            list.reload();
            stock.reload();
          }}
        />
      )}

      {tab === 'waste' && mayWrite && (
        <WasteForm
          setup={setup.data!}
          ingredients={ingredients}
          onDone={() => {
            list.reload();
            stock.reload();
          }}
        />
      )}

      {tab === 'counts' && <Counts storerooms={setup.data?.storerooms ?? []} />}
    </>
  );
}

// =====================================================================
// A delivery
// =====================================================================

function ReceiveForm({
  setup,
  ingredients,
  onDone,
}: {
  setup: { storerooms: Storeroom[] };
  ingredients: Ingredient[];
  onDone: () => void;
}) {
  const { currency } = useSession();
  const [ingredientId, setIngredientId] = useState('');
  const [storeroomId, setStoreroomId] = useState(
    setup.storerooms.find((s) => s.is_default)?.id ?? setup.storerooms[0]?.id ?? '',
  );
  const [quantity, setQuantity] = useState('1');
  const [cost, setCost] = useState('');
  const [reference, setReference] = useState('');

  const ing = ingredients.find((i) => i.id === ingredientId);
  const qty = Number(quantity);
  const costMinor = Math.round(Number(cost) * 100);
  const stockQty = ing && Number.isFinite(qty) ? qty * Number(ing.stock_per_purchase) : 0;
  const unitCost = stockQty > 0 && Number.isFinite(costMinor) ? costMinor / stockQty : 0;

  const save = useMutation(async () => {
    const r = await api('/v1/kitchen/receipts', {
      method: 'POST',
      body: {
        ingredient_id: ingredientId,
        storeroom_id: storeroomId,
        purchase_quantity: qty,
        cost_minor: costMinor,
        reference: reference || undefined,
      },
    });
    setQuantity('1');
    setCost('');
    setReference('');
    onDone();
    return r;
  });

  return (
    <Card title="A Delivery Came In">
      <ErrorBanner error={save.error} />
      <p className="muted small">
        Key it the way the invoice reads — how many cases, and what the line came to. The shelf
        figure below is what that turns into; if it looks wrong, the pack size on the ingredient
        is wrong, and that is worth fixing before the food cost is.
      </p>

      <div className="form-rows">
        <label className="field">
          <span>What arrived</span>
          <select value={ingredientId} onChange={(e) => setIngredientId(e.target.value)}>
            <option value="">Choose…</option>
            {ingredients.map((i) => (
              <option key={i.id} value={i.id}>
                {i.name} ({i.purchase_description ?? i.stock_unit})
              </option>
            ))}
          </select>
        </label>

        <label className="field">
          <span>Onto which shelf</span>
          <select value={storeroomId} onChange={(e) => setStoreroomId(e.target.value)}>
            {setup.storerooms.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </label>

        <label className="field">
          <span>How many {ing?.purchase_description ?? 'units'}</span>
          <input value={quantity} onChange={(e) => setQuantity(e.target.value)} inputMode="decimal" />
        </label>

        <label className="field">
          <span>What the line came to</span>
          <input
            value={cost}
            onChange={(e) => setCost(e.target.value)}
            inputMode="decimal"
            placeholder="370.00"
          />
          <div className="hint">The extended total, not the unit price.</div>
        </label>

        <label className="field">
          <span>Invoice number</span>
          <input value={reference} onChange={(e) => setReference(e.target.value)} />
        </label>
      </div>

      {ing && stockQty > 0 && (
        <div className="notice">
          <strong>
            That is {nf.format(stockQty)} {ing.stock_unit_plural ?? ing.stock_unit} on the shelf
            {unitCost > 0 && <> at {perUnit(unitCost, ing.stock_unit_code)}</>}.
          </strong>
          {Number(ing.yield_percent) !== 100 && (
            <p className="muted small">
              At a {nf.format(Number(ing.yield_percent))}% yield, the part that reaches a plate
              costs {perUnit(unitCost / (Number(ing.yield_percent) / 100), ing.stock_unit_code)}.
            </p>
          )}
          <p className="muted small">
            It posts {money(costMinor, currency)} to inventory against goods received not
            invoiced. The vendor bill clears that when it arrives.
          </p>
        </div>
      )}

      <div className="actions">
        <button
          disabled={!ingredientId || !storeroomId || !(qty > 0) || !(costMinor >= 0) || save.busy}
          onClick={() => save.run()}
        >
          {save.busy ? 'Receiving…' : 'Receive it'}
        </button>
      </div>
    </Card>
  );
}

// =====================================================================
// Waste
// =====================================================================

function WasteForm({
  setup,
  ingredients,
  onDone,
}: {
  setup: { storerooms: Storeroom[] };
  ingredients: Ingredient[];
  onDone: () => void;
}) {
  const [ingredientId, setIngredientId] = useState('');
  const [storeroomId, setStoreroomId] = useState(
    setup.storerooms.find((s) => s.is_default)?.id ?? setup.storerooms[0]?.id ?? '',
  );
  const [quantity, setQuantity] = useState('');
  const [reason, setReason] = useState('');

  const ing = ingredients.find((i) => i.id === ingredientId);

  const save = useMutation(async () => {
    const r = await api('/v1/kitchen/waste', {
      method: 'POST',
      body: {
        ingredient_id: ingredientId,
        storeroom_id: storeroomId,
        quantity: Number(quantity),
        reason,
      },
    });
    setQuantity('');
    setReason('');
    onDone();
    return r;
  });

  return (
    <Card title="Something Went in the Bin">
      <ErrorBanner error={save.error} />
      <p className="muted small">
        Logged separately from every other kind of loss, and it is worth the thirty seconds: a
        club that records its waste can tell over-ordering from over-portioning. A club that
        leaves it to the stocktake has one number called variance and no idea what is inside it.
      </p>

      <div className="form-rows">
        <label className="field">
          <span>What</span>
          <select value={ingredientId} onChange={(e) => setIngredientId(e.target.value)}>
            <option value="">Choose…</option>
            {ingredients.map((i) => (
              <option key={i.id} value={i.id}>{i.name}</option>
            ))}
          </select>
        </label>

        <label className="field">
          <span>Off which shelf</span>
          <select value={storeroomId} onChange={(e) => setStoreroomId(e.target.value)}>
            {setup.storerooms.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </label>

        <label className="field">
          <span>How much{ing ? ` (${ing.stock_unit_plural ?? ing.stock_unit})` : ''}</span>
          <input value={quantity} onChange={(e) => setQuantity(e.target.value)} inputMode="decimal" />
        </label>

        <label className="field">
          <span>What happened</span>
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Case turned in the walk-in"
          />
          <div className="hint">
            Required. Without it this is indistinguishable from a keying error.
          </div>
        </label>
      </div>

      <div className="actions">
        <button
          disabled={!ingredientId || !(Number(quantity) > 0) || !reason.trim() || save.busy}
          onClick={() => save.run()}
        >
          {save.busy ? 'Recording…' : 'Record it'}
        </button>
      </div>
    </Card>
  );
}

// =====================================================================
// Counting
// =====================================================================

function Counts({ storerooms }: { storerooms: Storeroom[] }) {
  const { atLeast } = useSession();
  const [open, setOpen] = useState<string | null>(null);
  const [storeroomId, setStoreroomId] = useState(
    storerooms.find((s) => s.is_default)?.id ?? storerooms[0]?.id ?? '',
  );

  const counts = useApi<{ counts: any[] }>('/v1/kitchen/counts');

  const start = useMutation(async () => {
    const r = await api<{ id: string }>('/v1/kitchen/counts', {
      method: 'POST',
      body: { storeroom_id: storeroomId },
    });
    counts.reload();
    setOpen(r.id);
    return r;
  });

  return (
    <>
      <Card title="Counts">
        <ErrorBanner error={counts.error ?? start.error} />
        <p className="muted small">
          The one number in this module nobody derived. Everything else says what the kitchen
          <em> should</em> have used; the count is what it actually did, and the gap between the
          two is the thing worth managing.
        </p>

        <div className="row" style={{ gap: 12, alignItems: 'flex-end' }}>
          <label className="field" style={{ marginBottom: 0 }}>
            <span>Count which shelf</span>
            <select value={storeroomId} onChange={(e) => setStoreroomId(e.target.value)}>
              {storerooms.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </label>
          <button disabled={!storeroomId || start.busy} onClick={() => start.run()}>
            {start.busy ? 'Starting…' : 'Start a count'}
          </button>
        </div>

        {(counts.data?.counts ?? []).length === 0 ? (
          <Empty>Nothing has been counted yet.</Empty>
        ) : (
          <div className="table">
            <table className="data data--cards">
              <thead>
                <tr>
                  <th>Shelf</th>
                  <th>Date</th>
                  <th className="num">Lines</th>
                  <th>State</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {(counts.data?.counts ?? []).map((c) => (
                  <tr key={c.id}>
                    <td data-label="Shelf" className="cell--title">{c.storeroom}</td>
                    <td data-label="Date" className="muted">{c.counted_on}</td>
                    <td data-label="Lines" className="num">{c.lines_counted}</td>
                    <td data-label="State">
                      <Status tone={c.status === 'posted' ? 'good' : c.status === 'open' ? 'warning' : 'neutral'}>
                        {c.status === 'open' ? 'Counting' : c.status === 'posted' ? 'Posted' : 'Abandoned'}
                      </Status>
                    </td>
                    <td className="cell--action">
                      <button className="linkish" onClick={() => setOpen(open === c.id ? null : c.id)}>
                        {open === c.id ? 'Close' : 'Open'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {open && (
        <CountSheet
          countId={open}
          mayPost={atLeast('controller')}
          onChanged={() => counts.reload()}
        />
      )}
    </>
  );
}

function CountSheet({
  countId,
  mayPost,
  onChanged,
}: {
  countId: string;
  mayPost: boolean;
  onChanged: () => void;
}) {
  const [draft, setDraft] = useState<Record<string, string>>({});
  const { data, error, loading, reload } = useApi<{ count: any; sheet: any[] }>(
    `/v1/kitchen/counts/${countId}`,
    [countId],
  );

  const save = useMutation(async (row: any) => {
    const value = draft[row.ingredient_id];
    const r = await api(`/v1/kitchen/counts/${countId}/lines`, {
      method: 'PUT',
      body: { ingredient_id: row.ingredient_id, counted_qty: Number(value) },
    });
    reload();
    return r;
  });

  const post = useMutation(async () => {
    const r = await api(`/v1/kitchen/counts/${countId}/post`, { method: 'POST' });
    reload();
    onChanged();
    return r;
  });

  if (loading && !data) return <Loading rows={2} />;
  if (!data) return null;

  const openSheet = data.count.status === 'open';
  const counted = data.sheet.filter((r) => r.counted).length;

  return (
    <Card title={`${data.count.storeroom} — ${data.count.counted_on}`}>
      <ErrorBanner error={error ?? save.error ?? post.error} />

      <p className="muted small">
        {counted} of {data.sheet.length} counted. Everything the shelf has ever held is listed,
        including what the books say is at zero — that is exactly the one worth walking over to
        check.
      </p>

      <div className="table">
        <table className="data">
          <thead>
            <tr>
              <th>What</th>
              <th className="num">Books say</th>
              <th className="num">Counted</th>
              <th className="num">Difference</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data.sheet.map((r) => {
              const value = draft[r.ingredient_id] ?? (r.counted ? String(r.counted_qty) : '');
              return (
                <tr key={r.ingredient_id}>
                  <td className="cell--title">
                    {r.ingredient}
                    <span className="muted small"> · {r.stock_unit_code}</span>
                  </td>
                  <td className="num muted">{nf.format(Number(r.book_qty))}</td>
                  <td className="num">
                    {openSheet ? (
                      <input
                        value={value}
                        inputMode="decimal"
                        style={{ maxWidth: 90, textAlign: 'right' }}
                        onChange={(e) =>
                          setDraft((d) => ({ ...d, [r.ingredient_id]: e.target.value }))
                        }
                      />
                    ) : (
                      nf.format(Number(r.counted_qty ?? 0))
                    )}
                  </td>
                  <td className="num">
                    {r.variance === null || r.variance === undefined ? (
                      <span className="muted">—</span>
                    ) : (
                      <Status tone={Number(r.variance) === 0 ? 'good' : 'warning'}>
                        {Number(r.variance) > 0 ? '+' : ''}
                        {nf.format(Number(r.variance))}
                      </Status>
                    )}
                  </td>
                  <td className="cell--action">
                    {openSheet && value !== '' && (
                      <button
                        className="linkish"
                        disabled={save.busy}
                        onClick={() => save.run(r)}
                      >
                        Save
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {openSheet && mayPost && (
        <div className="actions">
          <button disabled={counted === 0 || post.busy} onClick={() => post.run()}>
            {post.busy ? 'Posting…' : 'Post the count'}
          </button>
          <span className="muted small">
            The books come up to the shelf and the difference posts to cost of sales. The sheet
            cannot be edited afterwards — it is the evidence.
          </span>
        </div>
      )}
    </Card>
  );
}
